import { useCallback, useMemo } from 'react';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestStatusEnum, PublishedStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useResearchTeamMembers } from './use-research-team-members.hook';

interface ISection {
    name: string;
    show: boolean;
}

interface IUsePublishApprovedReturn {
    getLeadInvestigator: (dataRequest: IDataRequest) => string;
    getPublishedText: (dataRequest: IDataRequest) => string;
    getFundingSources: (dataRequest: IDataRequest) => string;
    getDataRequestIdToShow: (dataRequest: IDataRequest) => string;
    sectionsToShow: (dataRequest: IDataRequest, isConfig: boolean) => ISection[];
}

export function usePublishApproved(): IUsePublishApprovedReturn {
    const { getLeadResearcher } = useResearchTeamMembers();

    const getDataRequestIdToShow = useCallback((dataRequest: IDataRequest) => {
        const idToShow = dataRequest?.publishing?.publishingData.vivliDataRequestIdInfo ?? dataRequest.dataRequestDoi;
        return idToShow;
    }, []);

    const getLeadInvestigator = useCallback(
        (dataRequest: IDataRequest) => {
            const leadInvestigator = getLeadResearcher(dataRequest?.researchTeam);
            return `${leadInvestigator?.firstName} ${leadInvestigator?.lastName}, ${leadInvestigator?.employerOrAffiliation}`;
        },
        [getLeadResearcher]
    );

    const getPublishedText = useCallback((dataRequest: IDataRequest) => {
        return dataRequest?.publishing?.publishingRules.publishedStatus === PublishedStatusEnum.Published
            ? 'This landing page is PUBLISHED'
            : 'This landing page is NOT PUBLISHED';
    }, []);

    const getFundingSources = useCallback((dataRequest: IDataRequest) => {
        let fundingSourceString = '';
        if (dataRequest.funding.researchGrantsFromGovernments) {
            fundingSourceString += `Government Research Grants - ${dataRequest.funding.researchGrantsFromGovernmentsDetail}\n`;
        }
        if (dataRequest.funding.fundingFromEmployers) {
            fundingSourceString += `Employment Contract Funding - ${dataRequest.funding.fundingFromEmployersDetail}\n`;
        }
        if (dataRequest.funding.additionalContractsAndConsultancies) {
            fundingSourceString += `Additional Contractor Funding - ${dataRequest.funding.additionalContractsAndConsultanciesDetail}\n`;
        }
        if (dataRequest.funding.fundingFromCommercialOrganizations) {
            fundingSourceString += `Commercial Organization Funding - ${dataRequest.funding.fundingFromCommercialOrganizationsDetail}\n`;
        }

        fundingSourceString = fundingSourceString === '' ? 'None' : fundingSourceString;

        return fundingSourceString;
    }, []);

    const showPi = useCallback((dataRequest: IDataRequest, isConfig: boolean) => {
        return dataRequest?.publishing?.publishingRules.showLeadInvestigator || isConfig;
    }, []);

    const showTitle = useCallback((dataRequest: IDataRequest, isConfig: boolean) => {
        return dataRequest?.publishing?.publishingRules.showTitleOfProposedResearch || isConfig;
    }, []);

    const sectionsToShow = useCallback(
        (dataRequest: IDataRequest, isConfig: boolean) => {
            const showedSections: ISection[] = [
                {
                    name: 'withdrawn',
                    show:
                        dataRequest?.publishing.publishingRules.showWithdrawn ||
                        (isConfig && dataRequest.status === DataRequestStatusEnum.Cancelled),
                },
                {
                    name: 'leadInvestigator',
                    show: showPi(dataRequest, isConfig),
                },
                {
                    name: 'titleOfProposedResearch',
                    show: showTitle(dataRequest, isConfig),
                },
                {
                    name: 'vivliDataRequestIdInfo',
                    show: dataRequest?.publishing.publishingRules.showVivliDataRequestIdInfo || isConfig,
                },
                {
                    name: 'fundingSource',
                    show: dataRequest?.publishing.publishingRules.showFundingSource || isConfig,
                },
                {
                    name: 'potentialConflictsOfInterest',
                    show: dataRequest?.publishing.publishingRules.showPotentialConflictsOfInterest || isConfig,
                },
                {
                    name: 'statisticalAnalysisPlan',
                    show: dataRequest?.publishing.publishingRules.showStatisticalAnalysisPlan || isConfig,
                },
                {
                    //in this case don't show if empty
                    name: 'additionalInformation',
                    show:
                        isConfig ||
                        (dataRequest?.publishing.publishingRules.showAdditionalInformation &&
                            dataRequest?.publishing.publishingData.additionalInformation !== null &&
                            dataRequest?.publishing.publishingData.additionalInformation !== ''),
                },
                {
                    //should not show even in config page if no references yet,
                    // show in preview if there are publishable references
                    name: 'publicDisclosures',
                    show:
                        (isConfig && dataRequest.citationReferences?.length > 0) ||
                        dataRequest.publishing?.publishingRules.citationPublishingRules?.filter((a) => a.publishable).length > 0,
                },
                {
                    //in this case don't show if empty
                    name: 'summaryOfResults',
                    show:
                        isConfig ||
                        (dataRequest?.publishing.publishingRules.showSummaryOfResults &&
                            dataRequest?.publishing.publishingData.summaryOfResults !== null &&
                            dataRequest?.publishing.publishingData.summaryOfResults !== ''),
                },
                {
                    name: 'laySummary',
                    show: dataRequest?.publishing.publishingRules.showLaySummary || isConfig,
                },
            ];

            return showedSections;
        },
        [showPi, showTitle]
    );

    return useMemo(
        () => ({
            getLeadInvestigator,
            getPublishedText,
            getFundingSources,
            getDataRequestIdToShow,
            sectionsToShow,
        }),
        [getLeadInvestigator, getPublishedText, getFundingSources, getDataRequestIdToShow, sectionsToShow]
    );
}
